export type NativePlatform = 'ios' | 'android';
export type NativeStoreProvider = 'apple_app_store' | 'google_play';

export interface NativeCapabilities {
  contractVersion: 1;
  platform: NativePlatform;
  commerce: 'storekit2' | 'play_billing' | 'unavailable';
  location: 'foreground' | 'foreground_background' | 'unavailable';
  attestation: 'app_attest' | 'play_integrity' | 'unavailable';
}

export interface NativePurchaseRequest {
  productCode: string;
  providerProductRef: string;
  accountBinding: string;
  idempotencyKey: string;
}

export interface NativePurchaseResult {
  provider: NativeStoreProvider;
  productCode: string;
  transactionId: string;
  receipt: string;
}

export interface NativePosition {
  latitude: number;
  longitude: number;
  accuracyM: number;
  recordedAt: string;
  speedMps?: number;
}

export interface NativeAttestationRequest {
  action: 'payment' | 'journey_start' | 'journey_submit';
  nonce: string;
}

export interface NativeAttestationResult {
  provider: 'app_attest' | 'play_integrity';
  token: string;
}

/**
 * Versioned contract implemented by the signed iOS/Android host. The web bundle never
 * fabricates capabilities and never treats attestation, location or a receipt as authority.
 */
export interface AztecRealmsNativeBridgeV1 {
  getCapabilities(): Promise<NativeCapabilities>;
  purchase(request: NativePurchaseRequest): Promise<NativePurchaseResult>;
  getForegroundPosition(): Promise<NativePosition>;
  startBackgroundJourney(input: {
    consentVersion: string;
    purpose: 'exploration';
  }): Promise<{ started: true }>;
  drainBackgroundJourney(): Promise<{ samples: readonly NativePosition[] }>;
  stopBackgroundJourney(): Promise<{ stopped: true }>;
  requestAttestation(request: NativeAttestationRequest): Promise<NativeAttestationResult>;
}

export class NativeCapabilityUnavailableError extends Error {
  constructor(capability: 'commerce' | 'location' | 'attestation') {
    super(`${capability} is unavailable in this application host`);
    this.name = 'NativeCapabilityUnavailableError';
  }
}

export type NativeHost = { AztecRealmsNativeV1?: unknown };

const boundedIdentifier = /^[A-Za-z0-9][A-Za-z0-9_.:-]{1,127}$/;

function isBridge(value: unknown): value is AztecRealmsNativeBridgeV1 {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return [
    'getCapabilities',
    'purchase',
    'getForegroundPosition',
    'startBackgroundJourney',
    'drainBackgroundJourney',
    'stopBackgroundJourney',
    'requestAttestation',
  ].every((name) => typeof candidate[name] === 'function');
}

export function resolveNativeBridge(host: object): AztecRealmsNativeBridgeV1 | undefined {
  const candidate = (host as NativeHost).AztecRealmsNativeV1;
  return isBridge(candidate) ? candidate : undefined;
}

export function assertNativeCapabilities(value: NativeCapabilities): NativeCapabilities {
  const validCommerce =
    (value.platform === 'ios' && ['storekit2', 'unavailable'].includes(value.commerce)) ||
    (value.platform === 'android' && ['play_billing', 'unavailable'].includes(value.commerce));
  if (
    value.contractVersion !== 1 ||
    !['ios', 'android'].includes(value.platform) ||
    !validCommerce ||
    !['foreground', 'foreground_background', 'unavailable'].includes(value.location) ||
    !['app_attest', 'play_integrity', 'unavailable'].includes(value.attestation)
  ) {
    throw new Error('The native host returned invalid capability metadata');
  }
  return value;
}

export function assertNativePurchaseResult(
  result: NativePurchaseResult,
  requestedProductCode: string,
): NativePurchaseResult {
  if (
    !['apple_app_store', 'google_play'].includes(result.provider) ||
    result.productCode !== requestedProductCode ||
    !boundedIdentifier.test(result.productCode) ||
    !boundedIdentifier.test(result.transactionId) ||
    result.receipt.length < 16 ||
    result.receipt.length > 32_768
  ) {
    throw new Error('The native store returned an invalid purchase contract');
  }
  return result;
}

export function assertNativePosition(value: NativePosition): NativePosition {
  const recordedAt = Date.parse(value.recordedAt);
  if (
    !Number.isFinite(value.latitude) ||
    value.latitude < -90 ||
    value.latitude > 90 ||
    !Number.isFinite(value.longitude) ||
    value.longitude < -180 ||
    value.longitude > 180 ||
    !Number.isFinite(value.accuracyM) ||
    value.accuracyM <= 0 ||
    value.accuracyM > 10_000 ||
    !Number.isFinite(recordedAt)
  ) {
    throw new Error('The native host returned an invalid location sample');
  }
  return value;
}

export const unavailableNativeCapabilities = Object.freeze({
  contractVersion: 1,
  platform: 'android',
  commerce: 'unavailable',
  location: 'unavailable',
  attestation: 'unavailable',
} satisfies NativeCapabilities);
